import EVENT from '../enums/event.js';
import KEY from '../enums/key.js';
import STATE from '../enums/state.js';
import TabTrapper from '../utils/accessibility.tab-trapper.js';
import dispatchEvent from '../utils/dispatch-event.js';
import onDocumentReady from '../utils/on-document-ready.js';

const selectors = {
  focusable:
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]',
};

class Modal {
  /**
   *
   * @param {Element} $element
   */
  constructor($element) {
    onDocumentReady(() => {
      this.$modal = $element;
      this.$focusableElements = this.$modal.querySelectorAll(
        selectors.focusable
      );
      this.$openTriggers = [...document.querySelectorAll(`[data-modal-open]`)];
      this.$closeTriggers = [
        ...document.querySelectorAll(`[data-modal-close]`),
      ];
      this.$toggleTriggers = [
        ...document.querySelectorAll(`[data-modal-toggle]`),
      ];

      this.name = $element.dataset.modal;

      this.registerEventListeners();
      this.hideFocusableElements();
    });
  }

  /**
   * @returns {'closed'|'open'}
   */
  get state() {
    return this.$modal.dataset.state;
  }

  set state(state) {
    this.$modal.dataset.state = state;
  }

  registerEventListeners() {
    document.addEventListener(EVENT.MODAL.OPEN, event => {
      if (event.detail.modal !== this.name) {
        this.closeModal();
        return;
      }

      if (this.state !== STATE.MODAL.OPEN) {
        this.openModal();
      }
    });

    document.addEventListener(EVENT.MODAL.CLOSE, event => {
      if (event.detail.modal !== this.name) {
        this.closeModal();
        return;
      }

      if (this.state === STATE.MODAL.OPEN) {
        this.closeModal();
      }
    });

    document.addEventListener(EVENT.MODAL.TOGGLE, event => {
      if (event.detail.modal !== this.name) {
        this.closeModal();
        return;
      }

      if (this.state === STATE.MODAL.OPEN) {
        this.closeModal();
        return;
      }

      this.openModal();
    });

    document.addEventListener(EVENT.OVERLAY.DEACTIVATE, () =>
      this.closeModal()
    );

    this.$openTriggers.forEach($openTrigger => {
      $openTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.MODAL.OPEN, $openTrigger, {
          detail: {
            modal: $openTrigger.dataset.modalOpen,
          },
        });
      });
    });

    this.$closeTriggers.forEach($closeTrigger => {
      $closeTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.MODAL.CLOSE, $closeTrigger, {
          detail: {
            modal: $closeTrigger.dataset.modalClose,
          },
        });
      });
    });

    this.$toggleTriggers.forEach($toggleTrigger => {
      $toggleTrigger.addEventListener('click', event => {
        event.preventDefault();

        dispatchEvent(EVENT.MODAL.TOGGLE, $toggleTrigger, {
          detail: {
            modal: $toggleTrigger.dataset.modalToggle,
          },
        });
      });
    });
  }

  closeModal() {
    if (this.state === STATE.MODAL.CLOSED) {
      return;
    }

    this.state = STATE.MODAL.CLOSED;
    this.hideFocusableElements();

    dispatchEvent(EVENT.OVERLAY.DEACTIVATE, this.$modal);

    // Controlling focus
    if (this.focusElement) {
      this.focusElement.focus();
    }

    if (this.tabTrapper) {
      this.tabTrapper.close();
    }

    document.removeEventListener('keydown', event => {
      if (event.key === KEY.ESCAPE) {
        this.closeModal();
      }
    });

    window.removeEventListener(
      'scroll',
      () => {
        this.closeModal();
      },
      {
        passive: true,
      }
    );
  }

  openModal() {
    this.showFocusableElements();

    this.state = STATE.MODAL.OPEN;

    dispatchEvent(EVENT.OVERLAY.ACTIVATE, this.$modal, {
      detail: {
        lockScroll: false,
      },
    });

    // Controlling focus
    this.focusElement = document.activeElement;
    if (this.$focusableElements.length > 0) {
      this.$focusableElements[0].focus();
    }

    this.tabTrapper = new TabTrapper(this.$modal);

    document.addEventListener('keydown', event => {
      if (event.key === KEY.ESCAPE) {
        this.closeModal();
      }
    });

    window.addEventListener(
      'scroll',
      () => {
        this.closeModal();
      },
      {
        passive: true,
      }
    );
  }

  hideFocusableElements() {
    this.$focusableElements.forEach(focusableElement => {
      const el = focusableElement;
      el.tabIndex = -1;
    });
  }

  showFocusableElements() {
    this.$focusableElements.forEach(focusableElement => {
      const el = focusableElement;
      el.tabIndex = 0;
    });
  }
}

export default Modal;
